import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  PhoneAndroid as PhoneIcon,
  Security as SecurityIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Sms as SmsIcon,
  VerifiedUser as VerifiedIcon
} from '@mui/icons-material';
import { twoFactorService } from '../services/twoFactorService';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

const STEPS = ['Enter Phone Number', 'Verify Code', 'Complete'];

/**
 * TwoFactorSetup can be used in two modes:
 * 1. Standalone: Manages its own dialogs (when open/onClose not provided)
 * 2. Controlled dialog: Used as a dialog component (when open/onClose are provided)
 */
const TwoFactorSetup = ({ onComplete, onCancel, open, onClose }) => {
  useAuth(); // Ensure user is authenticated
  const [activeStep, setActiveStep] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [displayName, setDisplayName] = useState('My Phone');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [enrolledFactors, setEnrolledFactors] = useState([]);
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [factorToRemove, setFactorToRemove] = useState(null);

  // Check if being used as a controlled dialog
  const isControlledDialog = typeof open !== 'undefined';

  // Load enrolled factors on mount
  useEffect(() => {
    loadEnrolledFactors();
  }, []);

  const loadEnrolledFactors = () => {
    const factors = twoFactorService.getEnrolledFactors();
    setEnrolledFactors(factors);
  };

  const handleSendCode = async () => {
    if (!phoneNumber) {
      setError('Please enter a phone number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formattedPhone = twoFactorService.formatPhoneNumber(phoneNumber);
      await twoFactorService.startEnrollment(formattedPhone, 'recaptcha-container');
      setActiveStep(1);
      setSuccess('Verification code sent to your phone');
    } catch (err) {
      logger.error('Error sending code:', err);
      setError(err.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await twoFactorService.completeEnrollment(verificationCode, displayName);
      setActiveStep(2);
      setSuccess('Two-factor authentication has been enabled!');
      loadEnrolledFactors();

      // Notify parent component
      if (onComplete) {
        setTimeout(() => {
          onComplete();
        }, 2000);
      }
    } catch (err) {
      logger.error('Error verifying code:', err);
      setError(err.message || 'Failed to verify code');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFactor = async () => {
    if (!factorToRemove) return;

    setLoading(true);
    setError('');

    try {
      await twoFactorService.unenroll(factorToRemove.uid);
      setSuccess('Two-factor authentication has been removed');
      loadEnrolledFactors();
      setShowRemoveDialog(false);
      setFactorToRemove(null);
    } catch (err) {
      logger.error('Error removing 2FA:', err);
      setError(err.message || 'Failed to remove two-factor authentication');
    } finally {
      setLoading(false);
    }
  };

  const resetEnrollment = () => {
    setActiveStep(0);
    setPhoneNumber('');
    setVerificationCode('');
    setError('');
    setSuccess('');
    twoFactorService.clearRecaptcha();
  };

  const formatPhoneDisplay = (phone) => {
    // Format for display: (123) 456-7890
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  const renderEnrolledFactors = () => {
    if (enrolledFactors.length === 0) {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          Two-factor authentication is not enabled. Add a phone number to secure your account.
        </Alert>
      );
    }

    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <VerifiedIcon color="success" />
            <Typography variant="subtitle1" fontWeight="bold">
              Two-Factor Authentication Enabled
            </Typography>
            <Chip label="Active" color="success" size="small" />
          </Box>

          <List dense>
            {enrolledFactors.map((factor) => (
              <ListItem key={factor.uid}>
                <ListItemIcon>
                  <PhoneIcon />
                </ListItemIcon>
                <ListItemText
                  primary={factor.displayName}
                  secondary={`Enrolled: ${new Date(factor.enrollmentTime).toLocaleDateString()}`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    color="error"
                    onClick={() => {
                      setFactorToRemove(factor);
                      setShowRemoveDialog(true);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  };

  const renderEnrollmentFlow = () => (
    <Box>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && activeStep < 2 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Step 0: Phone Number */}
      {activeStep === 0 && (
        <Box>
          <Typography variant="body1" paragraph>
            Enter your phone number to receive verification codes when signing in.
          </Typography>

          <TextField
            fullWidth
            label="Phone Number"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="(555) 123-4567"
            disabled={loading}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon />
                </InputAdornment>
              )
            }}
            helperText="US phone numbers only. We'll send you a text message with a verification code."
          />

          <TextField
            fullWidth
            label="Device Name (optional)"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="My Phone"
            disabled={loading}
            sx={{ mb: 3 }}
            helperText="A friendly name to identify this device"
          />

          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              onClick={handleSendCode}
              disabled={loading || !phoneNumber}
              startIcon={loading ? <CircularProgress size={20} /> : <SmsIcon />}
            >
              {loading ? 'Sending...' : 'Send Verification Code'}
            </Button>
            {onCancel && (
              <Button variant="outlined" onClick={onCancel} disabled={loading}>
                Cancel
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Step 1: Verification Code */}
      {activeStep === 1 && (
        <Box>
          <Typography variant="body1" paragraph>
            Enter the 6-digit code sent to {formatPhoneDisplay(phoneNumber)}
          </Typography>

          <TextField
            fullWidth
            label="Verification Code"
            value={verificationCode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
              setVerificationCode(value);
            }}
            placeholder="123456"
            disabled={loading}
            sx={{ mb: 3 }}
            inputProps={{
              maxLength: 6,
              style: { letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.5em' }
            }}
            helperText="Check your text messages for the code"
          />

          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              onClick={handleVerifyCode}
              disabled={loading || verificationCode.length !== 6}
              startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </Button>
            <Button
              variant="outlined"
              onClick={resetEnrollment}
              disabled={loading}
            >
              Start Over
            </Button>
          </Box>

          <Box mt={2}>
            <Button
              variant="text"
              size="small"
              onClick={handleSendCode}
              disabled={loading}
            >
              Resend Code
            </Button>
          </Box>
        </Box>
      )}

      {/* Step 2: Complete */}
      {activeStep === 2 && (
        <Box textAlign="center">
          <VerifiedIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Two-Factor Authentication Enabled!
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Your account is now protected with two-factor authentication.
            You'll receive a verification code via SMS when signing in.
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              resetEnrollment();
              setShowEnrollDialog(false);
            }}
          >
            Done
          </Button>
        </Box>
      )}

      {/* Hidden reCAPTCHA container */}
      <div id="recaptcha-container" />
    </Box>
  );

  // Handle dialog close for controlled mode
  const handleControlledClose = () => {
    resetEnrollment();
    if (onClose) {
      onClose();
    }
  };

  // If used as a controlled dialog, render just the dialog
  if (isControlledDialog) {
    return (
      <Dialog
        open={open}
        onClose={handleControlledClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Set Up Two-Factor Authentication
        </DialogTitle>
        <DialogContent>
          {renderEnrollmentFlow()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleControlledClose} disabled={loading}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Standalone mode - manages its own dialogs
  return (
    <Box>
      {/* Current 2FA Status */}
      {renderEnrolledFactors()}

      {/* Add 2FA Button */}
      {enrolledFactors.length === 0 && (
        <Button
          variant="contained"
          startIcon={<SecurityIcon />}
          onClick={() => setShowEnrollDialog(true)}
          sx={{ mb: 2 }}
        >
          Enable Two-Factor Authentication
        </Button>
      )}

      {/* Enrollment Dialog */}
      <Dialog
        open={showEnrollDialog}
        onClose={() => {
          resetEnrollment();
          setShowEnrollDialog(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Set Up Two-Factor Authentication
        </DialogTitle>
        <DialogContent>
          {renderEnrollmentFlow()}
        </DialogContent>
      </Dialog>

      {/* Remove 2FA Confirmation Dialog */}
      <Dialog
        open={showRemoveDialog}
        onClose={() => {
          setShowRemoveDialog(false);
          setFactorToRemove(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle color="error">
          Remove Two-Factor Authentication?
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Removing two-factor authentication will make your account less secure.
          </Alert>
          <Typography variant="body1">
            Are you sure you want to remove "{factorToRemove?.displayName}" as a verification method?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowRemoveDialog(false);
              setFactorToRemove(null);
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRemoveFactor}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {loading ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TwoFactorSetup;
